/**
 * useDashboard hook
 * React Query hook for fetching student dashboard summary
 * Provides dashboard data with loading, error, and refetch capabilities
 */

import { useQuery } from '@tanstack/react-query';
import { getDashboardSummary } from '@/services/dashboard';
import type { DashboardSummary } from '@/types/dashboard';

/**
 * Return type for useDashboard hook
 */
export interface UseDashboardReturn {
  /** Dashboard summary data (undefined during loading) */
  dashboard: DashboardSummary | undefined;
  /** Loading state - true during initial fetch */
  isLoading: boolean;
  /** Error object if fetch failed */
  error: Error | null;
  /** Function to manually refetch */
  refetch: () => void;
}

/**
 * Hook for fetching and managing dashboard summary data
 * Uses React Query with 30s staleTime and retry: 1
 */
export function useDashboard(): UseDashboardReturn {
  const {
    data: dashboard,
    isLoading,
    error,
    refetch,
  } = useQuery<DashboardSummary, Error>({
    queryKey: ['user', 'dashboard'],
    queryFn: () => getDashboardSummary(),
    staleTime: 30000, // 30 seconds (consistent with usePebbles)
  });

  return {
    dashboard,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
